import React, { FunctionComponent, useEffect } from 'react';

import Badge from '@material-ui/core/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentAltLines as faMessagingIcon, faInbox as faInboxIcon } from '@fortawesome/pro-regular-svg-icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { InboxPath, TwoWaySMSPath } from 'routes/Paths';
import { useHasPremiumAccess, useOmnichannelInboxEnabled } from 'hooks';
import { fetchUnreadCountThunk } from '@spike/omnichannel-action';
import { fetchTwoWaySmsCountThunk } from 'actions/twoWaySms/twoWaySmsActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isNumber } from 'lodash';
import { Link as LinkRouter } from 'react-router-dom';
import HeaderIconLabel from 'components/UI/HeaderIconLabel';
import usePermissions from 'hooks/usePermissions';
import { useApiClientWrapper } from 'hooks';

interface Props {
    messageAmount?: number;
    onClick?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'width': 40,
            'height': 40,
            'cursor': 'pointer',
            'position': 'relative',
            'borderRadius': '50px',
            'display': 'inline-flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'backgroundColor': 'transparent',
            'transition': 'background-color 0.2s ease-out',

            '&:hover': {
                backgroundColor: '#ededed'
            }
        },
        customBadge: {
            top: 0,
            right: 26,
            zIndex: 0,
            left: 'auto',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1,
            fontWeight: 600,
            borderRadius: 3,
            transform: 'none',
            alignItems: 'center',
            padding: '2px 6px 1px',
            justifyContent: 'center',
            backgroundColor: '#EF4F57'
        },
        icon: {
            fontSize: 20,
            color: '#000000'
        }
    })
);

export const MessageIcon: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const hasTwoWaySmsPremiumAccess = useHasPremiumAccess('two_way_sms');
    const permissions = usePermissions();
    const omnichannelInboxEnabled = useOmnichannelInboxEnabled();
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const unifiedUnread = useSelector<RootState, number>(state => state.omnichannel.unreadTotals.total);
    const smsUnread = useSelector<RootState, number>(state => state.twoWaySms.messageAmount);

    const twoWaySmsEnabled = process.env.REACT_APP_TWO_WAY_SMS_ENABLED?.toLowerCase() === 'true';
    const canUseLegacyMessaging = twoWaySmsEnabled && hasTwoWaySmsPremiumAccess && permissions.twoWaySms;

    useEffect(() => {
        if (omnichannelInboxEnabled) {
            dispatch(fetchUnreadCountThunk(apiClientWrapper));
            return;
        }

        if (canUseLegacyMessaging) {
            dispatch(fetchTwoWaySmsCountThunk());
        }
        // eslint-disable-next-line
    }, [omnichannelInboxEnabled, canUseLegacyMessaging]);

    if (omnichannelInboxEnabled) {
        return (
            <LinkRouter to={InboxPath}>
                <HeaderIconLabel text="Inbox">
                    <Badge
                        onClick={props.onClick}
                        classes={{ badge: classes.customBadge }}
                        className={classes.container}
                        badgeContent={isNumber(unifiedUnread) ? unifiedUnread : 0}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                    >
                        <FontAwesomeIcon
                            className={classes.icon}
                            icon={faInboxIcon}
                        />
                    </Badge>
                </HeaderIconLabel>
            </LinkRouter>
        );
    }

    return canUseLegacyMessaging ? (
        <LinkRouter to={TwoWaySMSPath}>
            <HeaderIconLabel text="Messaging">
                <Badge
                    onClick={props.onClick}
                    classes={{ badge: classes.customBadge }}
                    className={classes.container}
                    badgeContent={isNumber(smsUnread) ? smsUnread : 0}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                >
                    <FontAwesomeIcon
                        className={classes.icon}
                        icon={faMessagingIcon}
                    />
                </Badge>
            </HeaderIconLabel>
        </LinkRouter>
    ) : (
        <></>
    );
};

export default MessageIcon;
